import type { GearItem, LevelSim } from "@highmarch/domain";
import { audio } from "../audio/audio";
import type { MusicTrack } from "../audio/audio";
import { PostFx } from "../render/postfx";
import { rareNight } from "../render/rareLook";
import { RareWorld } from "../render/rareWorld";
import type { SceneRig } from "../render/scene";
import { groundAt } from "../render/terrain";
import { SHINY_SCREEN, SPARKLE_PX } from "../render/views/rareShiny";
import { STATUS_COLOUR, IMMUNE_STATUS } from "../render/views/enemyLook";
import { RareSignal, RareDef, immunityKinds, readRareSignals, rareNow } from "../rare/rareApi";
import type { HudView } from "../ui/hud";
import { RareBanner, RareChestReveal, RareEscape, RARE_BANNER, plainBar, rareBar } from "../ui/rareUi";
import { ScreenFlash } from "../ui/screenFlash";
import { reducedVfx } from "../ui/settings";
import type { HitStop } from "./hitStop";
import type { LevelScene } from "./levelScene";

/** 0xRRGGBB → the sprite VFX's [r, g, b] (0–1). */
const rgb = (hex: number): [number, number, number] => [((hex >> 16) & 255) / 255, ((hex >> 8) & 255) / 255, (hex & 255) / 255];

/**
 * The rare spawn as a big deal (M36 §5). Reads the rare's signals each tick (rare/rareApi.ts — the sim's events) and
 * stages them; presentation only, it never writes the sim.
 *
 * Spawn: the stinger's swell, then on its hit (`RARE_BANNER.SlamMs`) a gold screen flash, an exposure punch, a camera
 * push toward the portal and a trauma kick; the banner with the portrait and Captain Bram's line; the night eases in
 * (SceneRig.rareGlow, rareLook.ts) with the world sparkles (rareWorld.ts) and a gold frame; the rare bed replaces the
 * battle bed; the boss bar turns gold (ui/rareUi.ts). The creature itself shines (render/views/rareShiny.ts).
 * Kill: a slow-motion beat, a white-gold flash and burst at the body, the kill stinger, RARE SLAIN!, then the chest
 * reveal flies to the HUD while the night lifts through the sun-break and the battle bed comes back.
 * Escape: a red flash, a heavy shake, a low-pass beat and THE … ESCAPED; the night drains and the bed falls away (the
 * defeat screen follows: the sim has lost the level).
 * IMMUNE popups: one per enemy per tower per second (the sim's own limit, kept here too).
 */
export class RareMoment {
  private readonly banner: RareBanner;
  private readonly chest: RareChestReveal;
  private readonly escape: RareEscape;
  private readonly frameEl: HTMLDivElement;
  private world: RareWorld | null = null;
  private readonly signals: RareSignal[] = [];
  private def: RareDef | null = null;
  /** The view the camera push left, to ease back to once the banner has had its moment. */
  private camBack: { x: number; z: number; zoom: number } | null = null;
  private timers: number[] = [];
  /** Real seconds of the slow-motion kill beat still to run, and its depth. */
  private slowLeft = 0;
  private readonly immuneAt = new Map<number, number>();
  private disposed = false;
  private resumeChecked = false;
  /** The bed that was playing before the rare's (it comes back after). */
  private bedBefore: MusicTrack | null = null;

  constructor(private readonly sim: LevelSim, private readonly rig: SceneRig, private readonly scene: LevelScene, private readonly hud: HudView, private readonly hitStop: HitStop, uiRoot: HTMLElement) {
    this.banner = new RareBanner(uiRoot);
    this.escape = new RareEscape(uiRoot);
    this.chest = new RareChestReveal(uiRoot, () => hud.root.getBoundingClientRect());
    this.frameEl = document.createElement("div");
    this.frameEl.className = "rare-frame";
    uiRoot.prepend(this.frameEl);
  }

  /**
   * The gear the rare's chest banked (boot MetaFlow.rareGearNow: banks it now if the sim's end-of-tick hook has not yet,
   * once per chest), for the reveal. Empty when nothing is wired (headless tools).
   */
  rareGear: () => readonly GearItem[] = () => [];

  /** The rare is on the field. */
  get active(): boolean { return this.def !== null; }

  /** After a sim tick or a command: the signals from `from` on. */
  observe(from: number): void {
    if (this.disposed) return;
    this.signals.length = 0;
    readRareSignals(this.sim, from, this.signals);
    for (const s of this.signals) {
      if (s.kind === "spawned") this.spawned(s.def);
      else if (s.kind === "killed") this.killed(s.enemyId, s.def, s.chestRarity);
      else if (s.kind === "escaped") this.escaped(s.def);
      else this.immune(s.enemyId, s.towerId, s.immunity);
    }
  }

  private later(ms: number, fn: () => void): void { this.timers.push(window.setTimeout(() => { if (!this.disposed) fn(); }, ms)); }

  /** `quiet`: a battle resumed with the rare already on the field — its look, bed and bar, without the entrance. */
  private spawned(def: RareDef, quiet = false): void {
    this.def = def;
    const reduced = reducedVfx();
    this.bedBefore = audio.track;
    this.rig.rareGlow.spawned(rareNight(def.sky));
    this.frameEl.style.setProperty("--rare-a", `#${def.sparkA.toString(16).padStart(6, "0")}`);
    this.frameEl.classList.add("on");
    rareBar(this.hud.bossBar, def);
    if (!this.world) {
      const g = this.sim.grid;
      this.world = new RareWorld(g.width, g.height, groundAt);
      this.rig.scene.add(this.world.points);
    }
    this.world.setColours(def.sparkA, def.sparkB);
    if (quiet) {
      if (audio.track === "battle" || audio.track === "boss") audio.music("rare");
      return;
    }
    audio.play("rareSpawn");
    this.later(RARE_BANNER.SlamMs - 200, () => { if (audio.track === "battle" || audio.track === "boss") audio.music("rare"); });
    this.banner.show(def);
    // the camera leans toward the portal as the creature rises, and the hit lands with the stinger's
    const portal = this.sim.road.portal;
    const view = this.rig.iso.viewState();
    this.camBack = { x: view.x, z: view.z, zoom: view.zoom };
    if (!reduced) this.rig.iso.followTo({ x: view.x + (portal.x - view.x) * 0.7, z: view.z + (portal.y - view.z) * 0.7, zoom: Math.max(0.24, view.zoom * 0.84) }, 0.55);
    this.later(RARE_BANNER.SlamMs, () => {
      const fx = this.scene.router?.vfx, drawn = this.scene.enemies.rareDrawn();
      if (fx && drawn) {
        const up = drawn.feet.clone(); up.y += drawn.height * 0.5;
        fx.flash(up, 2.0, rgb(def.sparkB));
        fx.sparkles(up, reducedVfx() ? 10 : 46, { color: rgb(def.sparkA), radius: 1.0, rise: 1.6, life: 1.4, size: 0.14 });
        fx.light(up, rgb(def.sparkA), 5, 3, 2.2);
      }
      ScreenFlash.flash(`#${def.sparkB.toString(16).padStart(6, "0")}`, 0.5, 420);
      PostFx.exposurePunch(1.35, 520);
      if (!reducedVfx()) this.rig.iso.addTrauma(0.42);
      this.scene.grid.foliage.gustAround(portal, 4);
    });
    // hand the view back where the player had it once the banner has said its piece (unless they took it meanwhile)
    this.later(RARE_BANNER.SlamMs + 2200, () => { if (this.camBack && !reducedVfx()) this.rig.iso.followTo(this.camBack, 0.9); this.camBack = null; });
  }

  private killed(enemyId: number, def: RareDef, chestRarity: number | null): void {
    const reduced = reducedVfx();
    this.banner.hide();
    const at = this.scene.enemies.rareDrawn()?.feet.clone() ?? this.scene.enemies.feet(enemyId)?.clone() ?? null;
    audio.play("rareKill");
    if (!reduced) {
      this.hitStop.request(180, { ramp: true, force: true });
      this.slowLeft = RareMoment.SlowSeconds;
      this.rig.iso.addTrauma(0.55);
    }
    ScreenFlash.flash("#fff6d8", 0.62, 520);
    PostFx.exposurePunch(1.5, 700);
    if (at && this.scene.router) {
      // the celebration at the body: the tier-up's shockwave, spiral fountain and halo rings in the rare's gold, a burst
      // of its two sparkle colours, a flash and a light that lingers while it dissolves
      const fx = this.scene.router.vfx, a = rgb(def.sparkA), b = rgb(def.sparkB);
      const h = this.scene.enemies.rareDrawn()?.height ?? 0.9, up = at.clone(); up.y += h * 0.5;
      fx.towerLevelUp(at, h, { color: a });
      fx.sparkles(up, reduced ? 14 : 70, { color: a, radius: 0.9, rise: 1.4, life: 1.3, size: 0.16 });
      if (!reduced) fx.sparkles(up, 40, { color: b, radius: 0.6, rise: 2.0, life: 1.6, size: 0.12 });
      fx.flash(up, 2.4, b);
      fx.light(up, a, 4.5, 3.5, 1.6);
    }
    this.hud.showCallout("RARE SLAIN!", def.name, "bossDown");
    this.rig.rareGlow.killed();
    this.frameEl.classList.remove("on");
    this.later(1300, () => this.chest.show(def, chestRarity, this.sim.balance, this.rareGear(), this.sim.rareChest?.gold ?? 0));
    this.later(1500, () => this.bedBack());
    this.later(RareMoment.BarBackMs, () => { if (this.def === null) plainBar(this.hud.bossBar); });   // M37: the level's own boss after it is a boss again, not "RARE"
    this.def = null;
  }

  private escaped(def: RareDef): void {
    const reduced = reducedVfx();
    this.banner.hide();
    ScreenFlash.flash("#ff2a1a", 0.55, 600);
    if (!reduced) { this.rig.iso.addTrauma(0.75); this.hitStop.request(120, { force: true }); }
    audio.play("lose");
    this.escape.show(def);
    this.rig.rareGlow.escaped();
    this.frameEl.classList.remove("on");
    this.bedBack();
    this.later(RareMoment.BarBackMs, () => { if (this.def === null) plainBar(this.hud.bossBar); });
    this.def = null;
  }

  private bedBack(): void {
    if (audio.track === "rare") audio.music(this.bedBefore === "rare" || this.bedBefore === null ? "battle" : this.bedBefore);
  }

  /** IMMUNE over the enemy, at most once a second per enemy and tower. */
  private immune(enemyId: number, towerId: number, mask: number): void {
    const key = enemyId * 4096 + towerId, now = performance.now();
    if (now - (this.immuneAt.get(key) ?? -Infinity) < 1000) return;
    this.immuneAt.set(key, now);
    if (this.immuneAt.size > 256) this.immuneAt.clear();
    const kind = immunityKinds(mask)[0] ?? "physical";
    this.scene.enemies.floatOver(enemyId, "IMMUNE", STATUS_COLOUR[IMMUNE_STATUS[kind]], 15);
  }

  /** Real seconds of the kill's slow motion, and how slow (sim and particles), easing back to 1× (`slowScale`). */
  static readonly SlowSeconds = 1.4;
  static readonly SlowFrom = 0.25;
  /** After a rare is gone, the boss bar (hidden by then) turns back into a plain one for the level's own boss (M37). */
  static readonly BarBackMs = 1800;

  /** The kill's slow-motion multiplier this frame (1 when none): the frame loop multiplies the sim's time by it. */
  slowScale(dt: number): number {
    if (this.slowLeft <= 0) return 1;
    this.slowLeft = Math.max(0, this.slowLeft - dt);
    const k = 1 - this.slowLeft / RareMoment.SlowSeconds;
    return RareMoment.SlowFrom + (1 - RareMoment.SlowFrom) * k * k;
  }

  /** Every frame: the world sparkles follow the night's weight; the sprite shaders learn the drawing buffer's size. */
  frame(dt: number): void {
    if (this.disposed) return;
    if (!this.resumeChecked) {                       // a resumed battle with the rare already walking: no RareSpawned comes
      this.resumeChecked = true;
      const r = rareNow(this.sim);
      if (r && !this.def && !this.sim.isOver) this.spawned(r.def, true);
    }
    SPARKLE_PX.value = Math.max(1, this.rig.canvas.height / 2);
    const drawn = this.scene.enemies.rareDrawn();
    if (drawn) {                                     // the body glitter's scale (rareShiny.ts glitterCells)
      const cam = this.rig.camera;
      SHINY_SCREEN.pxPerUnit = (cam.projectionMatrix.elements[5] * SPARKLE_PX.value) / Math.max(0.5, cam.position.distanceTo(drawn.centre));
    }
    const w = this.rig.rareGlow.weights;
    this.world?.update(dt, w.sparkle, reducedVfx());
    // the rare is gone from the sim with no signal (a rebuilt level, a resume): let the look and the bed go
    if (this.def && rareNow(this.sim) === null && !this.sim.isOver && this.scene.enemies.rareDrawn() === null) {
      this.rig.rareGlow.escaped(); this.frameEl.classList.remove("on"); this.bedBack(); this.def = null;
    }
  }

  /** The level ended: the banner and the reveal stay only if they are mid-flight; nothing new starts. */
  stop(): void { this.banner.hide(); this.slowLeft = 0; }

  dispose(): void {
    this.disposed = true;
    for (const t of this.timers) window.clearTimeout(t);
    this.timers = [];
    this.banner.hide(); this.chest.hide(); this.escape.hide();
    this.frameEl.remove();
    this.world?.dispose(); this.world = null;
    this.rig.rareGlow.reset();
    plainBar(this.hud.bossBar);
  }
}
